
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useStoreManagementStore } from "@/store/storeManagementStore";

export function useInvitationActions() {
  const { revokeInvitation, addEmployee } = useStoreManagementStore();
  const [resendingIds, setResendingIds] = useState<string[]>([]);
  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [invitationToDelete, setInvitationToDelete] = useState<any | null>(null);
  const { toast } = useToast();
  
  const handleResendInvitation = async (invitation: any) => {
    // Add invitation ID to processing state
    setResendingIds((prev) => [...prev, invitation.id]);

    try {
      // First revoke the existing invitation
      await revokeInvitation(invitation.id);
      
      // Then create a new invitation
      await addEmployee({
        storeId: invitation.store_id,
        firstName: invitation.first_name,
        lastName: invitation.last_name,
        email: invitation.email,
        role: invitation.role,
        address: invitation.address,
        street: invitation.street,
        streetNumber: invitation.street_number,
        city: invitation.city,
        postcode: invitation.postcode
      });
      
      toast({
        title: "Invitation Resent",
        description: `Invitation has been resent to ${invitation.email}`,
      });
    } catch (error) {
      console.error("Error resending invitation:", error);
      toast({
        title: "Error",
        description: "Failed to resend invitation",
        variant: "destructive"
      });
    } finally {
      // Remove invitation ID from processing state
      setResendingIds((prev) => prev.filter(id => id !== invitation.id));
    }
  };

  const prepareDeleteInvitation = (invitation: any) => {
    setInvitationToDelete(invitation);
  };

  const handleDeleteInvitation = async () => {
    if (!invitationToDelete) return;
    
    // Add invitation ID to deleting state
    setDeletingIds((prev) => [...prev, invitationToDelete.id]);

    try {
      // Revoke the invitation
      await revokeInvitation(invitationToDelete.id);
      
      toast({
        title: "Invitation Deleted",
        description: `Invitation for ${invitationToDelete.email} has been deleted`,
      });
      // Clear the invitation to delete after successful deletion
      setInvitationToDelete(null);
    } catch (error) {
      console.error("Error deleting invitation:", error);
      toast({
        title: "Error",
        description: "Failed to delete invitation",
        variant: "destructive"
      });
    } finally {
      // Remove invitation ID from deleting state
      setDeletingIds((prev) => prev.filter(id => id !== invitationToDelete.id));
    }
  };
  
  const cancelDeleteInvitation = () => {
    setInvitationToDelete(null);
  };

  return {
    resendingIds,
    deletingIds,
    invitationToDelete,
    handleResendInvitation,
    prepareDeleteInvitation,
    handleDeleteInvitation,
    cancelDeleteInvitation
  };
}
